/**
 * ForkEditor — edit a chain fork. Modify any step's title / script /
 * sources. Submit for admin review when ready. Withdraw to private to
 * resume editing. Owner-only (or admin).
 *
 * Lead's brief: "Forked chains are private to the user unless submitted
 * for review. Forked chains display attribution to the original chain."
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GitBranch, Loader2, Save, Send, ArrowLeft, RotateCcw, Trash2, AlertTriangle, FileText } from 'lucide-react';
import api from '../lib/api';

const STEP_KEYS = ['origin', 'change_a', 'change_b', 'contradiction', 'modern_pattern'];
const STEP_LABELS = {
  origin: 'Origin',
  change_a: 'Change A',
  change_b: 'Change B',
  contradiction: 'Contradiction',
  modern_pattern: 'Modern Pattern',
};

export default function ForkEditor() {
  const navigate = useNavigate();
  const { forkId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editScript, setEditScript] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [error, setError] = useState('');

  const reload = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api.get(`/forks/${forkId}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load fork.');
    }
    setBusy(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [forkId]);

  const fork = data?.fork;
  const original = data?.original_chain;
  const overrides = fork?.step_overrides || {};
  const modified = useMemo(() => new Set(fork?.modified_steps || []), [fork]);

  const startEdit = (key) => {
    const ov = overrides[key] || {};
    const orig = original?.[key] || {};
    setEditTitle(ov.title ?? orig.title ?? '');
    setEditScript(ov.script ?? orig.script ?? orig.content ?? '');
    setEditingKey(key);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditTitle('');
    setEditScript('');
  };

  const saveStep = async () => {
    if (!editingKey) return;
    setSavingKey(editingKey);
    setActionMsg('');
    try {
      // Capture values BEFORE setEditingKey resets the inputs to avoid
      // any chance of stale-state when render switches modes.
      const stepKey = editingKey;
      const titleVal = editTitle;
      const scriptVal = editScript;
      const r = await api.patch(`/forks/${forkId}/step`, {
        step_key: stepKey,
        title: titleVal,
        script: scriptVal,
      });
      // Optimistic local merge — eliminates GET-after-PATCH race entirely.
      // The PATCH response carries the canonical modified_steps array.
      setData((prev) => {
        if (!prev?.fork) return prev;
        const nextOverrides = { ...(prev.fork.step_overrides || {}) };
        nextOverrides[stepKey] = {
          ...(nextOverrides[stepKey] || {}),
          title: titleVal,
          script: scriptVal,
        };
        return {
          ...prev,
          fork: {
            ...prev.fork,
            step_overrides: nextOverrides,
            modified_steps: r.data?.modified_steps
              || Array.from(new Set([...(prev.fork.modified_steps || []), stepKey])),
            updated_at: r.data?.updated_at || prev.fork.updated_at,
          },
        };
      });
      setActionMsg(`Saved ${STEP_LABELS[stepKey]}.`);
      setEditingKey(null);
    } catch (e) {
      setActionMsg(e?.response?.data?.detail || 'Save failed.');
    }
    setSavingKey(null);
  };

  const submitForReview = async () => {
    setActionMsg('');
    try {
      const r = await api.post(`/forks/${forkId}/submit`);
      setData((prev) => prev?.fork ? {
        ...prev,
        fork: { ...prev.fork, fork_status: r.data?.fork_status || 'submitted' },
      } : prev);
      setActionMsg('Submitted for review.');
    } catch (e) {
      setActionMsg(e?.response?.data?.detail || 'Submit failed.');
    }
  };

  const withdraw = async () => {
    setActionMsg('');
    try {
      const r = await api.post(`/forks/${forkId}/withdraw`);
      setData((prev) => prev?.fork ? {
        ...prev,
        fork: { ...prev.fork, fork_status: r.data?.fork_status || 'private' },
      } : prev);
      setActionMsg('Withdrawn — back to private.');
    } catch (e) {
      setActionMsg(e?.response?.data?.detail || 'Withdraw failed.');
    }
  };

  const deleteFork = async () => {
    if (!window.confirm('Delete this fork permanently? This cannot be undone.')) return;
    try {
      await api.delete(`/forks/${forkId}`);
      navigate('/forks/me');
    } catch (e) {
      setActionMsg(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  if (busy && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
        <Loader2 size={20} className="animate-spin text-[#2F8A92]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#0A0A0A' }} data-testid="fork-editor-error">
        <div className="font-historical text-[#D4A84A] text-2xl font-light mb-2">Fork unavailable</div>
        <p className="font-mono text-sm text-white/65 mb-5 max-w-md text-center">{error}</p>
        <button
          onClick={() => navigate('/forks/me')}
          className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-5 py-2 hover:bg-[#D4A84A]/85"
        >
          MY FORKS
        </button>
      </div>
    );
  }
  if (!fork) return null;

  const status = fork.fork_status;
  const canEdit = status === 'private' || status === 'needs_revision';
  const lastNote = (fork.review_notes && fork.review_notes.length > 0)
    ? fork.review_notes[fork.review_notes.length - 1]
    : null;

  return (
    <div className="min-h-screen text-white pb-16" style={{ background: '#0A0A0A' }} data-testid="fork-editor">
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 font-mono text-[10px] tracking-widest uppercase text-white/55">
          <button onClick={() => navigate('/forks/me')} className="inline-flex items-center gap-1.5 hover:text-[#D4A84A]">
            <ArrowLeft size={11} /> My Forks
          </button>
          <span>·</span>
          <span data-testid="fork-status-badge" className={
            status === 'submitted'      ? 'text-[#2F8A92]'
            : status === 'needs_revision' ? 'text-amber-300'
            : status === 'published'    ? 'text-emerald-300'
            : 'text-white/65'
          }>
            STATUS: {status.toUpperCase().replace('_', ' ')}
          </span>
        </div>

        <div className="flex items-center gap-2 mb-1">
          <GitBranch size={16} className="text-[#D4A84A]" />
          <span className="font-hud text-[10px] md:text-xs tracking-widest uppercase text-[#D4A84A]">FORK · {fork.modified_steps?.length || 0} step(s) modified</span>
        </div>
        <h1 className="font-historical text-2xl md:text-4xl text-white font-light leading-tight">
          {fork.forked_from_chain_title}
        </h1>
        <div className="font-mono text-xs text-white/55 mt-2" data-testid="fork-attribution">
          Forked from chain id <span className="text-white/85">{fork.forked_from_chain_id?.slice(0, 8)}…</span> ·
          Era <span className="text-white/85">{fork.forked_from_chain_era || '—'}</span> ·
          Created {new Date(fork.created_at).toLocaleString()}
        </div>

        {/* Review note callout */}
        {status === 'needs_revision' && lastNote && (
          <div className="mt-4 border-l-2 border-amber-400 pl-3 py-2 bg-amber-500/5" data-testid="fork-revision-note">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-amber-300 mb-1">
              <AlertTriangle size={11} /> REVIEWER REQUESTED REVISION
            </div>
            <p className="font-mono text-xs md:text-sm text-white/85">{lastNote.reason}</p>
          </div>
        )}

        {actionMsg && (
          <div className="mt-3 font-mono text-xs text-[#2F8A92]" data-testid="fork-action-msg">{actionMsg}</div>
        )}

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-2 mt-5">
          {canEdit && (
            <button
              onClick={submitForReview}
              disabled={!fork.modified_steps || fork.modified_steps.length === 0}
              data-testid="fork-submit-btn"
              className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <Send size={12} /> SUBMIT FOR REVIEW
            </button>
          )}
          {status === 'submitted' && (
            <button
              onClick={withdraw}
              data-testid="fork-withdraw-btn"
              className="font-hud text-xs tracking-widest text-white/85 border border-[#D4A84A]/40 px-4 py-2 hover:text-[#D4A84A] inline-flex items-center gap-1.5"
            >
              <RotateCcw size={12} /> WITHDRAW
            </button>
          )}
          {status === 'private' && (
            <button
              onClick={deleteFork}
              data-testid="fork-delete-btn"
              className="font-hud text-xs tracking-widest text-red-300 border border-red-500/40 px-4 py-2 hover:text-red-200 inline-flex items-center gap-1.5"
            >
              <Trash2 size={12} /> DELETE
            </button>
          )}
        </div>

        {/* Steps */}
        <div className="mt-8 space-y-4" data-testid="fork-steps-list">
          {STEP_KEYS.map((key) => {
            const isModified = modified.has(key);
            const ov = overrides[key] || {};
            const orig = original?.[key] || {};
            const title = ov.title ?? orig.title ?? `Step: ${STEP_LABELS[key]}`;
            const script = ov.script ?? orig.script ?? orig.content ?? '';
            const isEditing = editingKey === key;
            return (
              <section
                key={key}
                data-testid={`fork-step-${key}`}
                data-modified={isModified ? 'true' : 'false'}
                className={`border rounded p-4 ${isModified ? 'border-[#2F8A92]/55 bg-[#2F8A92]/5' : 'border-[#D4A84A]/15'}`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <FileText size={13} className="text-[#D4A84A]" />
                    <span className="font-hud text-[10px] tracking-widest uppercase text-[#D4A84A]">{STEP_LABELS[key]}</span>
                    {isModified && (
                      <span className="font-mono text-[10px] tracking-widest text-[#2F8A92]" data-testid={`fork-step-modified-${key}`}>
                        · MODIFIED
                      </span>
                    )}
                  </div>
                  {!isEditing && canEdit && (
                    <button
                      onClick={() => startEdit(key)}
                      data-testid={`fork-edit-step-${key}`}
                      className="font-hud text-[10px] tracking-widest text-white/65 hover:text-[#D4A84A]"
                    >
                      EDIT
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      data-testid={`fork-edit-title-${key}`}
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Step title"
                      className="w-full bg-black/60 border border-[#D4A84A]/30 px-3 py-2 font-historical text-lg text-white focus:outline-none focus:border-[#D4A84A]/55"
                    />
                    <textarea
                      data-testid={`fork-edit-script-${key}`}
                      value={editScript}
                      onChange={(e) => setEditScript(e.target.value)}
                      placeholder="Step body / script"
                      rows={10}
                      className="w-full bg-black/60 border border-[#D4A84A]/30 px-3 py-2 font-mono text-xs text-white/90 focus:outline-none focus:border-[#D4A84A]/55"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveStep}
                        disabled={savingKey === key}
                        data-testid={`fork-save-step-${key}`}
                        className="font-hud text-[10px] tracking-widest text-[#0A0A0A] bg-[#2F8A92] px-3 py-1.5 hover:bg-[#2F8A92]/85 disabled:opacity-50 inline-flex items-center gap-1.5"
                      >
                        <Save size={11} /> {savingKey === key ? 'SAVING…' : 'SAVE'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="font-hud text-[10px] tracking-widest text-white/55 hover:text-[#D4A84A]"
                      >
                        CANCEL
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="font-historical text-lg md:text-xl text-white leading-snug">{title}</h3>
                    <p className="font-mono text-xs md:text-sm text-white/75 mt-1.5 whitespace-pre-line">
                      {script || <span className="text-white/40 italic">— no body — click EDIT to add —</span>}
                    </p>
                  </>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
